import { useState, useRef, RefObject } from "react";

/**
 * Local UI state for PageBase component
 * 
 * This replaces the UI-specific parts of PageBaseContext with local React state.
 * No need for a global context since this state is only used within PageBase.
 */

export interface PageBaseState {
	sidebarEnabled: boolean;
	sidebarOpen: boolean;
	notificationDrawerOpen: boolean;
	notificationCount: number;
	contentContainerRef?: RefObject<HTMLElement>;
}

/**
 * Hook for managing PageBase UI state
 * 
 * Provides local state management for sidebar, notifications, and content container ref.
 * This is simpler than a context provider and keeps state colocated with PageBase.
 * 
 * @example
 * ```typescript
 * const { state, setSidebarOpen, setNotificationDrawerOpen, contentContainerRef } = usePageBaseState();
 * ```
 */
export const usePageBaseState = () => {
	const contentContainerRef = useRef<HTMLElement>(null);
	
	const [state, setState] = useState<PageBaseState>({
		sidebarEnabled: true,
		sidebarOpen: true,
		notificationDrawerOpen: false,
		notificationCount: 0,
		contentContainerRef,
	});

	const setSidebarOpen = (open: boolean) => {
		setState(prev => ({ ...prev, sidebarOpen: open }));
	};

	const setSidebarEnabled = (enabled: boolean) => {
		setState(prev => ({ ...prev, sidebarEnabled: enabled }));
	};

	const setNotificationDrawerOpen = (open: boolean) => {
		setState(prev => ({ ...prev, notificationDrawerOpen: open }));
	};

	const setNotificationCount = (count: number) => {
		setState(prev => ({ ...prev, notificationCount: count }));
	};

	return {
		state,
		setState,
		setSidebarOpen,
		setSidebarEnabled,
		setNotificationDrawerOpen,
		setNotificationCount,
		contentContainerRef,
	};
};
